import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";

import { Box, Stack } from "@mui/material";
import { IFolder } from "store/types";

import { useWorkspace } from "../WorkspaceContext";
import { WorkspaceFolderRow } from "./WorkspaceFolderRow";

const ROW_HEIGHT = 36;

const slideInSx = {
  "@keyframes folderSlideIn": {
    from: { opacity: 0, transform: "translateY(-6px)" },
    to: { opacity: 1, transform: "translateY(0)" }
  },
  animation: "folderSlideIn 500ms ease"
};

function getRootFolders(folders: IFolder[]): IFolder[] {
  return folders.filter((f) => f.parent_id === null);
}

function getChildFolders(parentId: number, folders: IFolder[]): IFolder[] {
  return folders.filter((f) => f.parent_id === parentId);
}

function getVisibleFolderRows(
  folders: IFolder[],
  openedFolderIds: Set<number>,
  getRoot: (f: IFolder[]) => IFolder[],
  getChild: (id: number, f: IFolder[]) => IFolder[]
): Array<{ folder: IFolder; depth: number }> {
  const rows: Array<{ folder: IFolder; depth: number }> = [];
  const walk = (list: IFolder[], depth: number) => {
    for (const folder of list) {
      rows.push({ folder, depth });
      if (openedFolderIds.has(folder.id)) {
        walk(getChild(folder.id, folders), depth + 1);
      }
    }
  };
  walk(getRoot(folders), 0);
  return rows;
}

function getVisibleRowId(row: { folder: IFolder; depth: number }): string {
  return `global-folder-${row.folder.id}`;
}

interface Props {
  maxHeight: number;
}

export const FoldersList: React.FC<Props> = ({ maxHeight }) => {
  const { globalFolders, openedFolderIds, setOpenedFolderIds, currentFolderId } = useWorkspace();

  const toggleFolder = useCallback(
    (folderId: number) => {
      setOpenedFolderIds((prev) => {
        const next = new Set(prev);
        if (next.has(folderId)) {
          next.delete(folderId);
        } else {
          next.add(folderId);
        }
        return next;
      });
    },
    [setOpenedFolderIds]
  );

  const visibleRows = useMemo(
    () => getVisibleFolderRows(globalFolders, openedFolderIds, getRootFolders, getChildFolders),
    [globalFolders, openedFolderIds]
  );

  const prevVisibleIdsRef = useRef<Set<string>>(new Set(visibleRows.map(getVisibleRowId)));
  const [newlyVisibleIds, setNewlyVisibleIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const currentIds = new Set(visibleRows.map(getVisibleRowId));
    const prev = prevVisibleIdsRef.current;
    const newIds = new Set(Array.from(currentIds).filter((id) => !prev.has(id)));
    prevVisibleIdsRef.current = currentIds;
    if (!newIds.size) return;
    setNewlyVisibleIds(newIds);
    const timer = setTimeout(() => setNewlyVisibleIds(new Set()), 500);
    return () => clearTimeout(timer);
  }, [visibleRows]);

  const contentHeight = visibleRows.length * ROW_HEIGHT;
  const listHeight = Math.max(1, Math.min(maxHeight, contentHeight));
  const itemData = useMemo(
    () => ({
      visibleRows,
      globalFolders,
      openedFolderIds,
      toggleFolder,
      newlyVisibleIds,
      currentFolderId
    }),
    [visibleRows, globalFolders, openedFolderIds, toggleFolder, newlyVisibleIds, currentFolderId]
  );

  if (!globalFolders.length || maxHeight <= 0) {
    return null;
  }

  return (
    <Stack
      sx={{
        width: "100%",
        paddingLeft: "25px",
        height: listHeight,
        minHeight: 0,
        flexShrink: 0,
        transition: "height 0.2s ease"
      }}
    >
      <List
        height={listHeight}
        width="100%"
        itemCount={visibleRows.length}
        itemSize={ROW_HEIGHT}
        overscanCount={10}
        itemData={itemData}
      >
        {({ index, style, data }) => {
          const row = data.visibleRows[index];
          const rowId = getVisibleRowId(row);
          const isNew = data.newlyVisibleIds.has(rowId);
          return (
            <Box style={style} sx={isNew ? slideInSx : undefined}>
              <WorkspaceFolderRow
                folder={row.folder}
                depth={row.depth}
                folders={data.globalFolders}
                isOpen={data.openedFolderIds.has(row.folder.id)}
                isSelected={data.currentFolderId === row.folder.id}
                toggleFolder={data.toggleFolder}
              />
            </Box>
          );
        }}
      </List>
    </Stack>
  );
};
